import React, { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const KEY = 'ownerConsole.help.collapsed';
const readCollapsed = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { return {}; }
};

/**
 * Plain-language card at the top of every Owner Console section: what the
 * section controls, where the result shows up, and what happens after a save.
 * Open the first time, and each section remembers whether it was collapsed.
 */
export default function HowItWorks({ sectionKey, help }) {
  const [expanded, setExpanded] = useState(() => !readCollapsed()[sectionKey]);
  if (!help) return null;

  const toggle = (_, open) => {
    setExpanded(open);
    try {
      const all = readCollapsed();
      if (open) delete all[sectionKey]; else all[sectionKey] = true;
      localStorage.setItem(KEY, JSON.stringify(all));
    } catch (e) { /* remembering is a convenience only */ }
  };

  const Part = ({ title, body }) => (
    <Grid item xs={12} md={4}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>{title}</Typography>
      {(Array.isArray(body) ? body : [body]).map((line, i) => (
        <Typography key={i} variant="body2" sx={{ mb: 0.75 }}>{line}</Typography>
      ))}
    </Grid>
  );

  return (
    <Accordion expanded={expanded} onChange={toggle} disableGutters sx={{ mb: 3, bgcolor: 'action.hover', boxShadow: 'none', border: 1, borderColor: 'divider', '&::before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutlineIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">How this works</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Grid container spacing={2}>
          <Part title="What this controls" body={help.controls} />
          <Part title="Where it shows up" body={help.shows} />
          <Part title="After you save" body={help.after} />
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
